import React, { useState, useEffect } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultModal from 'components/Utils/DefaultModal';
import Icon from 'components/Utils/NewIcon';
import MenuItem from '@material-ui/core/MenuItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import IconButton from '@material-ui/core/IconButton';
import ParameterEditor from 'components/Utils/ParameterEditor';
const RichTextEditable = React.lazy(() => import('components/Utils/RichTextEditable'));
import TestParameterModal from '../TestParameterModal';
import { UncontrolledCollapse } from '../../Utils/Collapse';
import ProgressMessage from '../../Utils/ProgressMessage';
import ConfirmationDialogWrapper from '../../Utils/ConfirmationDialog/ConfirmationDialogWrapper';
import i18n from '../../../services/i18n';
import MiscUtils from 'services/MiscUtils';
import { DefaultSwitch } from 'services/UIUtils';
import './style.scss';

import type { HookCondition, EventSchema, HookAction } from '../../../generated/api';
import {
  HookConditionOperatorEnum,
  HookConditionLogicalOperatorEnum,
  HookActionTypeEnum,
} from '../../../generated/api';
import {
  ValidateHookActionPayload,
  ValidateHookActionPayloadActionTypeEnum,
} from 'generated/api/models/ValidateHookActionPayload';
import { HookActionTypes } from '../Helpers';
import type {
  TeamsActionConfig,
  EmailActionConfig,
  PushActionConfig,
  SmsActionConfig,
} from '../Helpers';

interface ConditionsAndActionsBuilderProps {
  conditions: HookCondition[];
  actions: HookAction[];
  eventSchema: EventSchema | null;
  onConditionsChange: (conditions: HookCondition[]) => void;
  onActionsChange: (actions: HookAction[]) => void;
  onClose: () => void;
  show: boolean;
  hookId?: string;
}

interface ActionFormProps {
  action: HookAction;
  eventSchema: EventSchema | null;
  onChange: (config: any) => void;
}

type AnyConfig = EmailActionConfig | SmsActionConfig | PushActionConfig | TeamsActionConfig;

const ConditionsAndActionsBuilder: React.FC<ConditionsAndActionsBuilderProps> = ({
  conditions,
  actions,
  eventSchema,
  onConditionsChange,
  onActionsChange,
  onClose,
  show,
  hookId,
}) => {
  const dispatch = useAppDispatch();
  const [localConditions, setLocalConditions] = useState<HookCondition[]>(conditions);
  const [localActions, setLocalActions] = useState<HookAction[]>(actions);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [showTestModal, setShowTestModal] = useState<boolean>(false);
  const [testActionIndex, setTestActionIndex] = useState<number>(-1);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false);

  useEffect(() => {
    setLocalConditions(conditions);
  }, [conditions]);

  useEffect(() => {
    setLocalActions(actions);
  }, [actions]);

  // ===== CONDITION LOGIC =====
  const operatorOptions = [
    {
      value: HookConditionOperatorEnum.Equals,
      label: 'Equals',
      description: 'Field equals the specified value',
    },
    {
      value: HookConditionOperatorEnum.NotEquals,
      label: 'Not equals',
      description: 'Field does not equal the specified value',
    },
    {
      value: HookConditionOperatorEnum.Contains,
      label: 'Contains',
      description: 'Field contains the specified text (case-insensitive)',
    },
    {
      value: HookConditionOperatorEnum.NotContains,
      label: 'Does not contain',
      description: 'Field does not contain the specified text',
    },
    {
      value: HookConditionOperatorEnum.GreaterThan,
      label: 'Greater than',
      description: 'Field is greater than the specified number',
    },
    {
      value: HookConditionOperatorEnum.LessThan,
      label: 'Less than',
      description: 'Field is less than the specified number',
    },
    {
      value: HookConditionOperatorEnum.Exists,
      label: 'Exists',
      description: 'Field has any value (not empty/null)',
    },
    {
      value: HookConditionOperatorEnum.NotExists,
      label: 'Does not exist',
      description: 'Field is empty or null',
    },
    // Field comparison operators
    {
      value: HookConditionOperatorEnum.EqualsField,
      label: 'Equals (field)',
      description: 'Field equals another field from the event',
    },
    {
      value: HookConditionOperatorEnum.NotEqualsField,
      label: 'Not equals (field)',
      description: 'Field does not equal another field from the event',
    },
    {
      value: HookConditionOperatorEnum.ContainsField,
      label: 'Contains (field)',
      description: 'Field contains text from another field',
    },
    {
      value: HookConditionOperatorEnum.NotContainsField,
      label: 'Does not contain (field)',
      description: 'Field does not contain text from another field',
    },
    {
      value: HookConditionOperatorEnum.GreaterThanField,
      label: 'Greater than (field)',
      description: 'Field is greater than another field',
    },
    {
      value: HookConditionOperatorEnum.LessThanField,
      label: 'Less than (field)',
      description: 'Field is less than another field',
    },
  ];

  const logicalOperatorOptions = [
    {
      value: HookConditionLogicalOperatorEnum.And,
      label: 'AND',
      description: 'Both this and the next condition must be true',
    },
    {
      value: HookConditionLogicalOperatorEnum.Or,
      label: 'OR',
      description: 'Either this or the next condition must be true',
    },
  ];

  const getOperatorsForFieldType = (fieldType: string) => {
    switch (fieldType?.toLowerCase()) {
      case 'enum':
        return operatorOptions.filter((op) => {
          const allowedOps: string[] = [
            HookConditionOperatorEnum.Equals,
            HookConditionOperatorEnum.NotEquals,
            HookConditionOperatorEnum.EqualsField,
            HookConditionOperatorEnum.NotEqualsField,
          ];
          return allowedOps.includes(op.value);
        });
      case 'string':
      case 'text':
        return operatorOptions.filter((op) => {
          const allowedOps: string[] = [
            HookConditionOperatorEnum.Equals,
            HookConditionOperatorEnum.NotEquals,
            HookConditionOperatorEnum.Contains,
            HookConditionOperatorEnum.NotContains,
            HookConditionOperatorEnum.Exists,
            HookConditionOperatorEnum.NotExists,
            HookConditionOperatorEnum.EqualsField,
            HookConditionOperatorEnum.NotEqualsField,
            HookConditionOperatorEnum.ContainsField,
            HookConditionOperatorEnum.NotContainsField,
          ];
          return allowedOps.includes(op.value);
        });
      case 'number':
      case 'integer':
      case 'float':
        return operatorOptions.filter((op) => {
          const allowedOps: string[] = [
            HookConditionOperatorEnum.Equals,
            HookConditionOperatorEnum.NotEquals,
            HookConditionOperatorEnum.GreaterThan,
            HookConditionOperatorEnum.LessThan,
            HookConditionOperatorEnum.Exists,
            HookConditionOperatorEnum.NotExists,
            HookConditionOperatorEnum.EqualsField,
            HookConditionOperatorEnum.NotEqualsField,
            HookConditionOperatorEnum.GreaterThanField,
            HookConditionOperatorEnum.LessThanField,
          ];
          return allowedOps.includes(op.value);
        });
      case 'boolean':
        return operatorOptions.filter((op) => {
          const allowedOps: string[] = [
            HookConditionOperatorEnum.Equals,
            HookConditionOperatorEnum.NotEquals,
            HookConditionOperatorEnum.Exists,
            HookConditionOperatorEnum.NotExists,
            HookConditionOperatorEnum.EqualsField,
            HookConditionOperatorEnum.NotEqualsField,
          ];
          return allowedOps.includes(op.value);
        });
      default:
        return operatorOptions;
    }
  };

  const needsValue = (operator: string) => {
    const noValueOps: string[] = [
      HookConditionOperatorEnum.Exists,
      HookConditionOperatorEnum.NotExists,
    ];
    return !noValueOps.includes(operator);
  };

  const isFieldOperator = (operator: string) => {
    const fieldOps: string[] = [
      HookConditionOperatorEnum.EqualsField,
      HookConditionOperatorEnum.NotEqualsField,
      HookConditionOperatorEnum.ContainsField,
      HookConditionOperatorEnum.NotContainsField,
      HookConditionOperatorEnum.GreaterThanField,
      HookConditionOperatorEnum.LessThanField,
    ];
    return fieldOps.includes(operator);
  };

  const addCondition = () => {
    const newCondition: HookCondition = {
      field: eventSchema?.parameters?.[0]?.name || '',
      operator: HookConditionOperatorEnum.Equals,
      value: '',
      logical_operator:
        localConditions.length > 0 ? HookConditionLogicalOperatorEnum.And : undefined,
    };

    setLocalConditions([...localConditions, newCondition]);
  };

  const updateCondition = (index: number, field: keyof HookCondition, value: any) => {
    const updated = [...localConditions];
    const currentCondition = updated[index];
    if (currentCondition) {
      updated[index] = {
        id: currentCondition.id,
        field: field === 'field' ? value : currentCondition.field,
        operator: field === 'operator' ? value : currentCondition.operator,
        value: field === 'value' ? value : currentCondition.value,
        logical_operator: field === 'logical_operator' ? value : currentCondition.logical_operator,
      };

      // Clear value if operator doesn't need it
      if (field === 'operator' && !needsValue(value)) {
        updated[index]!.value = undefined;
      }

      // Clear value when switching between field and non-field operators
      if (field === 'operator') {
        const wasFieldOperator = isFieldOperator(currentCondition.operator);
        const isNowFieldOperator = isFieldOperator(value);
        if (wasFieldOperator !== isNowFieldOperator) {
          updated[index]!.value = undefined;
        }
      }
    }

    setLocalConditions(updated);
  };

  const removeCondition = (index: number) => {
    const updated = localConditions.filter((_, i) => i !== index);
    setLocalConditions(updated);
  };

  const getFieldType = (fieldName: string): string => {
    if (!eventSchema?.parameters) return 'string';
    const parameter = eventSchema.parameters.find((p) => p.name === fieldName);
    return parameter?.type || 'string';
  };

  const getFieldParameter = (fieldName: string) => {
    if (!eventSchema?.parameters) return null;
    return eventSchema.parameters.find((p) => p.name === fieldName);
  };

  const getGroupedParameters = () => {
    if (!eventSchema?.parameters) return {};

    const parameters = eventSchema.parameters;
    const grouped: { [category: string]: typeof parameters } = {};

    parameters.forEach((param) => {
      const category = param.category || 'General';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category]!.push(param);
    });

    return grouped;
  };

  // ===== ACTION LOGIC =====
  const addAction = () => {
    const newAction: HookAction = {
      type: HookActionTypeEnum.Email,
      config: {
        to: '',
        subject: '',
        body: '',
        attached_document_parameters: [],
        generated_attachments: [],
      } as EmailActionConfig,
      enabled: true,
    };

    setLocalActions([...localActions, newAction]);
  };

  const updateAction = (index: number, data: Partial<HookAction>) => {
    const updated = [...localActions];
    const currentAction = updated[index];
    if (currentAction) {
      updated[index] = { ...currentAction, ...data } as HookAction;
    }
    setLocalActions(updated);
  };

  const removeAction = (index: number) => {
    const updated = localActions.filter((_, i) => i !== index);
    setLocalActions(updated);
  };

  const getActionTypeInfo = (type: HookActionTypeEnum) => {
    return HookActionTypes.find((t) => t.value === type);
  };

  const getValidationActionType = (
    type: HookActionTypeEnum,
  ): ValidateHookActionPayloadActionTypeEnum => {
    switch (type) {
      case HookActionTypeEnum.Email:
        return ValidateHookActionPayloadActionTypeEnum.Email;
      case HookActionTypeEnum.Sms:
        return ValidateHookActionPayloadActionTypeEnum.Sms;
      case HookActionTypeEnum.Push:
        return ValidateHookActionPayloadActionTypeEnum.Push;
      case HookActionTypeEnum.Teams:
        return ValidateHookActionPayloadActionTypeEnum.Teams;
      default:
        return ValidateHookActionPayloadActionTypeEnum.Email;
    }
  };

  const trimActionConfig = (config: any): any => {
    if (!config || typeof config !== 'object') return config;

    const trimmed = { ...config };
    Object.keys(trimmed).forEach((key) => {
      if (typeof trimmed[key] === 'string') {
        trimmed[key] = trimmed[key].trim();
      }
    });

    return trimmed;
  };

  const validateAllActions = async (
    actionsToValidate: HookAction[],
  ): Promise<{ isValid: boolean; errors: string[] }> => {
    const allErrors: string[] = [];

    for (let i = 0; i < actionsToValidate.length; i++) {
      const action = actionsToValidate[i];

      if (!action) continue;

      try {
        const validationPayload: ValidateHookActionPayload = {
          action_type: getValidationActionType(action.type),
          config: trimActionConfig(action.config),
        };

        const result = await dispatch(
          api.Hooks.validateHookAction({
            validateHookActionPayload: validationPayload,
          }),
          {
            errorTitle: `Validating Action ${i + 1}`,
          },
        );

        if (result && !result.valid && result.errors) {
          const actionInfo = getActionTypeInfo(action.type);
          const actionLabel = actionInfo?.label || action.type;

          result.errors.forEach((error) => {
            allErrors.push(`${actionLabel} Action ${i + 1}: ${error}`);
          });
        }
      } catch (error) {
        const actionInfo = getActionTypeInfo(action.type);
        const actionLabel = actionInfo?.label || action.type;
        allErrors.push(`${actionLabel} Action ${i + 1}: Validation failed - ${error}`);
      }
    }

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
    };
  };

  const openTestModal = (index: number) => {
    setTestActionIndex(index);
    setShowTestModal(true);
  };

  const renderActionForm = (action: HookAction, index: number) => {
    switch (action.type) {
      case HookActionTypeEnum.Email:
        return (
          <EmailActionForm
            action={action}
            eventSchema={eventSchema}
            onChange={(config) => updateAction(index, { config })}
          />
        );
      case HookActionTypeEnum.Sms:
        return (
          <SmsActionForm
            action={action}
            eventSchema={eventSchema}
            onChange={(config) => updateAction(index, { config })}
          />
        );
      case HookActionTypeEnum.Push:
        return (
          <PushActionForm
            action={action}
            eventSchema={eventSchema}
            onChange={(config) => updateAction(index, { config })}
          />
        );
      case HookActionTypeEnum.Teams:
        return (
          <TeamsActionForm
            action={action}
            eventSchema={eventSchema}
            onChange={(config) => updateAction(index, { config })}
          />
        );
      default:
        return null;
    }
  };

  // ===== SAVE LOGIC =====
  // Report every incomplete row instead of silently dropping it on save
  const getIncompleteRowErrors = (actions: HookAction[]): string[] => {
    const errors: string[] = [];

    localConditions.forEach((condition, index) => {
      const label = `Condition ${index + 1}`;
      if (!condition.field || !condition.operator) {
        errors.push(`${label} is missing a field or operator`);
        return;
      }
      if (needsValue(condition.operator)) {
        if (!condition.value) {
          errors.push(
            isFieldOperator(condition.operator)
              ? `${label} is missing the field to compare to`
              : `${label} is missing a value`,
          );
        } else if (
          isFieldOperator(condition.operator) &&
          !eventSchema?.parameters?.some((p) => p.name === condition.value)
        ) {
          errors.push(`${label} references a field that does not exist for this event`);
        }
      }
    });

    actions.forEach((action, index) => {
      const actionLabel = getActionTypeInfo(action.type)?.label || action.type;
      const label = `Action ${index + 1} (${actionLabel})`;
      const missing: string[] = [];
      switch (action.type) {
        case HookActionTypeEnum.Email: {
          const config = action.config as EmailActionConfig;
          if (!config.to) missing.push('a recipient');
          if (!config.subject) missing.push('a subject');
          break;
        }
        case HookActionTypeEnum.Sms: {
          const config = action.config as SmsActionConfig;
          if (!config.to) missing.push('a recipient');
          if (!config.message) missing.push('a message');
          break;
        }
        case HookActionTypeEnum.Push: {
          const config = action.config as PushActionConfig;
          if (!config.userId) missing.push('a user ID');
          if (!config.title) missing.push('a title');
          if (!config.message) missing.push('a message');
          break;
        }
        case HookActionTypeEnum.Teams: {
          const config = action.config as TeamsActionConfig;
          if (!config.message) missing.push('a message');
          break;
        }
      }
      if (missing.length) {
        errors.push(`${label} is missing ${missing.join(', ')}`);
      }
    });

    return errors;
  };

  const handleSave = async () => {
    const trimmedActions = localActions.map((action) => ({
      ...action,
      config: trimActionConfig(action.config),
    }));

    const incompleteErrors = getIncompleteRowErrors(trimmedActions);
    if (incompleteErrors.length > 0) {
      MiscUtils.showToastrError('Cannot Save Yet', incompleteErrors.join('. '));
      return;
    }

    // All rows complete — run server-side validation on the actions
    if (trimmedActions.length > 0) {
      setIsValidating(true);

      try {
        const originalActions = localActions;
        setLocalActions(trimmedActions);

        const validation = await validateAllActions(trimmedActions);

        if (validation.isValid) {
          onConditionsChange(localConditions);
          onActionsChange(trimmedActions);
          onClose();
        } else {
          MiscUtils.showToastrError('Hook Action Validation', validation.errors.join('. '));
          setLocalActions(originalActions);
        }
      } catch (error) {
        console.error('Validation error:', error);
        MiscUtils.showToastrError(
          'Hook Action Validation',
          'Failed to validate actions. Please check your configuration and try again.',
        );
      } finally {
        setIsValidating(false);
      }
    } else {
      onConditionsChange(localConditions);
      onActionsChange(trimmedActions);
      onClose();
    }
  };

  const hasUnsavedChanges = (): boolean =>
    JSON.stringify(localConditions) !== JSON.stringify(conditions) ||
    JSON.stringify(localActions) !== JSON.stringify(actions);

  const handleRequestClose = () => {
    if (hasUnsavedChanges()) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  if (!show) {
    return null;
  }

  const modalContent = (
    <div className="conditions-and-actions-builder-content">
      <div className="builder-body">
        {!eventSchema && (
          <div className="builder-notice">
            <Icon src="info-circle" />
            <p>Please select an event type first to configure conditions and actions.</p>
          </div>
        )}

        {eventSchema && (
          <>
            <div className="builder-info">
              <p>
                <strong>Event:</strong> {eventSchema.name || eventSchema.event_type}{' '}
                {eventSchema.description && <span> - {eventSchema.description}</span>}
              </p>
            </div>

            {/* CONDITIONS SECTION */}
            <div className="section-divider">
              <h4>
                <Icon src="filter" />
                Conditions
              </h4>
            </div>

            {localConditions.length === 0 && (
              <div className="builder-empty">
                <Icon src="filter" />
                <p>No conditions configured</p>
                <p>This hook will trigger for all events of the selected type.</p>
              </div>
            )}

            {localConditions.map((condition, index) => (
              <div key={index} className="condition-row">
                {index > 0 && (
                  <div className="logical-operator">
                    <DefaultSelectField
                      label={'condition-' + index}
                      value={condition.logical_operator || HookConditionLogicalOperatorEnum.And}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateCondition(index, 'logical_operator', e.target.value)
                      }
                      inputElements={logicalOperatorOptions.map((op) => (
                        <MenuItem key={op.value} value={op.value} title={op.description}>
                          {op.label}
                        </MenuItem>
                      ))}
                      style={{ width: '120px' }}
                    />
                  </div>
                )}

                <div className="condition-fields">
                  <DefaultSelectField
                    label="Field"
                    value={condition.field || 'none'}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateCondition(index, 'field', e.target.value)
                    }
                    inputElements={(() => {
                      const groupedParams = getGroupedParameters();
                      const elements: JSX.Element[] = [];

                      Object.keys(groupedParams)
                        .sort()
                        .forEach((category) => {
                          elements.push(
                            <ListSubheader key={`header-${category}`}>{category}</ListSubheader>,
                          );

                          groupedParams[category]?.forEach((param) => {
                            elements.push(
                              <MenuItem key={param.name} value={param.name}>
                                {param.description} (eg: {param.example})
                              </MenuItem>,
                            );
                          });
                        });

                      return elements;
                    })()}
                    placeholder="Select field"
                    required
                    style={{ flex: 1 }}
                  />

                  <DefaultSelectField
                    label="Operator"
                    value={condition.operator}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateCondition(index, 'operator', e.target.value)
                    }
                    inputElements={getOperatorsForFieldType(getFieldType(condition.field)).map(
                      (op) => (
                        <MenuItem key={op.value} value={op.value} title={op.description}>
                          {op.label}
                        </MenuItem>
                      ),
                    )}
                    placeholder="Select operator"
                    required
                    style={{ flex: 1 }}
                  />

                  {needsValue(condition.operator) &&
                    (() => {
                      const fieldParam = getFieldParameter(condition.field);
                      const isEnum = fieldParam?.type?.toLowerCase() === 'enum';
                      const enumValues = fieldParam?.enum_values || [];

                      // If using field comparison operator, show field selector
                      if (isFieldOperator(condition.operator)) {
                        const groupedParams = getGroupedParameters();
                        const elements: JSX.Element[] = [];

                        Object.keys(groupedParams)
                          .sort()
                          .forEach((category) => {
                            elements.push(
                              <ListSubheader key={`header-${category}`}>{category}</ListSubheader>,
                            );

                            groupedParams[category]?.forEach((param) => {
                              elements.push(
                                <MenuItem key={param.name} value={param.name}>
                                  {param.description} (eg: {param.example})
                                </MenuItem>,
                              );
                            });
                          });

                        return (
                          <DefaultSelectField
                            label="Compare to Field"
                            value={condition.value || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateCondition(index, 'value', e.target.value)
                            }
                            inputElements={elements}
                            placeholder="Select field to compare"
                            required
                            style={{ flex: 1 }}
                          />
                        );
                      }

                      // If enum type, show enum dropdown
                      if (isEnum && enumValues.length > 0) {
                        return (
                          <DefaultSelectField
                            label="Value"
                            value={condition.value || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateCondition(index, 'value', e.target.value)
                            }
                            inputElements={enumValues.map((enumValue) => (
                              <MenuItem key={enumValue} value={enumValue}>
                                {enumValue}
                              </MenuItem>
                            ))}
                            placeholder="Select value"
                            required
                            style={{ flex: 1 }}
                          />
                        );
                      }

                      // Default: text field for value input
                      return (
                        <DefaultTextField
                          label="Value"
                          value={condition.value || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateCondition(index, 'value', e.target.value)
                          }
                          placeholder={`Enter ${getFieldType(condition.field)} value`}
                          required
                          style={{ flex: 1 }}
                        />
                      );
                    })()}

                  <IconButton onClick={() => removeCondition(index)}>
                    <Icon src="trash" />
                  </IconButton>
                </div>
              </div>
            ))}

            {eventSchema.parameters && eventSchema.parameters.length > 0 && (
              <div className="add-button">
                <DefaultButton
                  name="Add Condition"
                  icon="plus"
                  onClick={addCondition}
                  style={{ marginTop: '16px' }}
                />
              </div>
            )}

            {/* ACTIONS SECTION */}
            <div className="section-divider">
              <h4>
                <Icon src="bolt" />
                Actions
              </h4>
            </div>

            {localActions.length === 0 && (
              <div className="builder-empty">
                <Icon src="bolt" />
                <p>No actions configured</p>
                <p>Add at least one action to make this hook functional.</p>
              </div>
            )}

            {localActions.map((action, index) => {
              return (
                <div key={index} className="action-row">
                  <UncontrolledCollapse
                    initialValue={false}
                    titleElement={
                      <div className="action-header">
                        <div className="action-header-content">
                          <div className="action-header-left" onClick={(e) => e.stopPropagation()}>
                            <DefaultSelectField
                              id="action-type"
                              value={action.type}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                e.stopPropagation();
                                const newType = e.target.value as HookActionTypeEnum;
                                updateAction(index, {
                                  type: newType,
                                  config: getDefaultConfig(newType),
                                });
                              }}
                              inputElements={HookActionTypes.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                  title={option.description}
                                >
                                  <div className="row gap-2">
                                    <Icon src={option.icon} height="16px" />
                                    <span>{option.label}</span>
                                  </div>
                                </MenuItem>
                              ))}
                              style={{ width: '140px' }}
                              variant="outlined"
                              size="small"
                            />
                          </div>

                          <div className="action-header-right">
                            {hookId && (
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTestModal(index);
                                }}
                                title="Test Action"
                                size="small"
                              >
                                <Icon src="play" height="16px" />
                              </IconButton>
                            )}
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                removeAction(index);
                              }}
                              title="Delete Action"
                              size="small"
                            >
                              <Icon src="trash" height="16px" />
                            </IconButton>
                          </div>
                        </div>
                      </div>
                    }
                  >
                    <div className="action-form" key={`${action.type}-${index}`}>
                      {renderActionForm(action, index)}
                    </div>
                  </UncontrolledCollapse>
                </div>
              );
            })}

            <div className="add-button">
              <DefaultButton
                name="Add Action"
                icon="plus"
                onClick={addAction}
                style={{ marginTop: '16px' }}
              />
            </div>
          </>
        )}
      </div>

      <div className="builder-footer">
        <DefaultButton
          name={
            isValidating
              ? 'Validating...'
              : `Save ${localConditions.length} Condition${
                  localConditions.length !== 1 ? 's' : ''
                } & ${localActions.length} Action${localActions.length !== 1 ? 's' : ''}`
          }
          icon="save"
          onClick={handleSave}
          color="blue"
          disabled={!eventSchema || isValidating}
          loading={isValidating}
          style={{ width: 'auto', minWidth: '250px' }}
        />
      </div>

      {/* Test Parameter Modal */}
      {showTestModal && localActions[testActionIndex] && (
        <TestParameterModal
          eventSchema={eventSchema}
          onClose={() => setShowTestModal(false)}
          action={localActions[testActionIndex]!}
          hookId={hookId}
        />
      )}

      {/* Confirm discarding unsaved builder changes */}
      <ConfirmationDialogWrapper
        open={showDiscardConfirm}
        title="Unsaved Changes"
        message="You have unsaved changes to the conditions and actions. Discard them?"
        onConfirm={() => {
          setShowDiscardConfirm(false);
          onClose();
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </div>
  );

  return (
    <DefaultModal
      title="Configure Conditions & Actions"
      className="conditions-and-actions-builder-modal large-modal"
      content={modalContent}
      wrap={true}
      padding={false}
      isOpen={show}
      onRequestClose={handleRequestClose}
    />
  );
};

// Helper function to get default config for each action type
const getDefaultConfig = (type: HookActionTypeEnum): AnyConfig => {
  switch (type) {
    case HookActionTypeEnum.Email:
      return {
        to: '',
        subject: '',
        body: '',
        attached_document_parameters: [],
        generated_attachments: [],
      } as EmailActionConfig;
    case HookActionTypeEnum.Sms:
      return { to: '', message: '' } as SmsActionConfig;
    case HookActionTypeEnum.Push:
      return { userId: '', userRole: 'patient', title: '', message: '' } as PushActionConfig;
    case HookActionTypeEnum.Teams:
      return { message: '', channel: '' } as TeamsActionConfig;
  }
};

// Helper function to filter parameters for contact fields (email/phone)
const getContactParameters = (parameters: any[], type: 'email' | 'phone') => {
  if (!parameters) return [];

  return parameters.filter((param) => {
    const name = param.name.toLowerCase();
    const desc = param.description?.toLowerCase() || '';

    if (type === 'email') {
      return (
        name.includes('email') ||
        desc.includes('email') ||
        (name.includes('patient') && (name.includes('email') || desc.includes('email'))) ||
        (name.includes('physician') && (name.includes('email') || desc.includes('email')))
      );
    } else if (type === 'phone') {
      return (
        name.includes('phone') ||
        name.includes('mobile') ||
        name.includes('cell') ||
        desc.includes('phone') ||
        desc.includes('mobile') ||
        (name.includes('patient') && (name.includes('phone') || desc.includes('phone'))) ||
        (name.includes('physician') && (name.includes('phone') || desc.includes('phone')))
      );
    }

    return false;
  });
};

// Individual Action Form Components
const EmailActionForm: React.FC<
  ActionFormProps & { onChange: (config: EmailActionConfig) => void }
> = ({ action, eventSchema, onChange }) => {
  const config: EmailActionConfig = {
    to: '',
    subject: '',
    body: '',
    attached_document_parameters: [],
    generated_attachments: [],
    ...action.config,
  };

  const updateField = (field: keyof EmailActionConfig, value: string | string[]) => {
    onChange({ ...config, [field]: value });
  };

  const emailParameters = getContactParameters(eventSchema?.parameters || [], 'email');

  // Get attachable parameters (documents from event data)
  const attachableParameters =
    eventSchema?.parameters?.filter((param) => param.attachable === true) || [];

  // Get attachment generators (dynamic files)
  const attachmentGenerators = eventSchema?.attachment_generators || [];

  // Handle document attachment checkbox toggle
  const toggleAttachment = (paramName: string) => {
    const currentAttachments = config.attached_document_parameters || [];
    const isAttached = currentAttachments.includes(paramName);

    if (isAttached) {
      // Remove from array
      updateField(
        'attached_document_parameters',
        currentAttachments.filter((name) => name !== paramName),
      );
    } else {
      // Add to array
      updateField('attached_document_parameters', [...currentAttachments, paramName]);
    }
  };

  // Handle generated attachment checkbox toggle
  const toggleGeneratedAttachment = (generatorName: string) => {
    const currentGenerated = config.generated_attachments || [];
    const isGenerated = currentGenerated.includes(generatorName);

    if (isGenerated) {
      // Remove from array
      updateField(
        'generated_attachments',
        currentGenerated.filter((name) => name !== generatorName),
      );
    } else {
      // Add to array
      updateField('generated_attachments', [...currentGenerated, generatorName]);
    }
  };

  // Helper to format generator name for display
  const formatGeneratorName = (generator: string): string => {
    // For example: "appointment.ics" -> "Appointment calendar file (.ics)"
    const ext = generator.split('.').pop() || '';
    const baseName = generator.split('.')[0] || generator;
    const formatted = baseName.charAt(0).toUpperCase() + baseName.slice(1);
    return `${formatted} file (.${ext})`;
  };

  return (
    <div className="email-action-form">
      <div className="field-label">To</div>
      <ParameterEditor
        value={config.to || ''}
        onChange={(value) => updateField('to', value)}
        placeholder="recipient@example.com or {{patient.email}}"
        parameters={emailParameters}
        showPreview={true}
        style={{ marginBottom: '12px' }}
      />
      <div className="field-label">Subject</div>
      <ParameterEditor
        value={config.subject || ''}
        onChange={(value) => updateField('subject', value)}
        placeholder="Email subject"
        parameters={eventSchema?.parameters || []}
        showPreview={true}
        style={{ marginBottom: '12px' }}
      />
      <div className="field-label">Body</div>
      <React.Suspense fallback={<ProgressMessage />}>
        <RichTextEditable
          value={config.body || ''}
          onChange={(value) => updateField('body', value)}
          placeholder="Email message content..."
          parameters={eventSchema?.parameters || []}
          style={{ minHeight: '200px' }}
        />
      </React.Suspense>

      {/* Attachments Section */}
      {(attachableParameters.length > 0 || attachmentGenerators.length > 0) && (
        <div className="attachments-section" style={{ marginTop: '16px' }}>
          <div className="field-label" style={{ marginBottom: '12px' }}>
            Attachments
          </div>

          {/* Section A: Document Attachments (from parameters) */}
          {attachableParameters.length > 0 && (
            <div style={{ marginBottom: attachmentGenerators.length > 0 ? '16px' : '0' }}>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#6b7280',
                  marginBottom: '12px',
                }}
              >
                Documents (from event data):
              </div>
              <div
                className="attachments-list"
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                {attachableParameters.map((param) => {
                  const isAttached = (config.attached_document_parameters || []).includes(
                    param.name,
                  );
                  return (
                    <DefaultSwitch
                      key={param.name}
                      label={param.description}
                      padded={false}
                      reversed={true}
                      checked={isAttached}
                      onChange={() => toggleAttachment(param.name)}
                      style={{ marginBottom: '4px' }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Section B: Generated Attachments (dynamic) */}
          {attachmentGenerators.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#6b7280',
                  marginBottom: '12px',
                }}
              >
                Generated Files:
              </div>
              <div
                className="attachments-list"
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                {attachmentGenerators.map((generator) => {
                  const isGenerated = (config.generated_attachments || []).includes(generator);
                  return (
                    <DefaultSwitch
                      key={generator}
                      label={formatGeneratorName(generator)}
                      padded={false}
                      reversed={true}
                      checked={isGenerated}
                      onChange={() => toggleGeneratedAttachment(generator)}
                      style={{ marginBottom: '4px' }}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SmsActionForm: React.FC<
  ActionFormProps & { onChange: (config: SmsActionConfig) => void }
> = ({ action, eventSchema, onChange }) => {
  const config: SmsActionConfig = {
    to: '',
    message: '',
    ...action.config,
  };

  const updateField = (field: keyof SmsActionConfig, value: string) => {
    onChange({ ...config, [field]: value });
  };

  const phoneParameters = getContactParameters(eventSchema?.parameters || [], 'phone');

  return (
    <div className="sms-action-form">
      <div className="field-label">To</div>
      <ParameterEditor
        value={config.to || ''}
        onChange={(value) => updateField('to', value)}
        placeholder="+1234567890 or {{patient.phone}}"
        parameters={phoneParameters}
        showPreview={true}
        style={{ marginBottom: '12px' }}
      />
      <div className="field-label">Message</div>
      <ParameterEditor
        value={config.message || ''}
        onChange={(value) => updateField('message', value)}
        placeholder="SMS message content"
        parameters={eventSchema?.parameters || []}
        showPreview={true}
      />
    </div>
  );
};

const PushActionForm: React.FC<
  ActionFormProps & { onChange: (config: PushActionConfig) => void }
> = ({ action, eventSchema, onChange }) => {
  const config: PushActionConfig = {
    userId: '',
    userRole: 'patient',
    title: '',
    message: '',
    ...action.config,
  };

  const updateField = (field: keyof PushActionConfig, value: string) => {
    onChange({ ...config, [field]: value });
  };

  const roleOptions = [
    { value: 'patient', label: 'Patient' },
    { value: 'physician', label: 'Physician' },
    { value: 'admin', label: i18n.staffTitle },
  ];

  return (
    <div className="push-action-form">
      <div className="row" style={{ gap: '12px', marginBottom: '12px' }}>
        <DefaultTextField
          label="User ID"
          value={config.userId || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            updateField('userId', e.target.value)
          }
          placeholder="user123"
          required
          style={{ flex: 1 }}
        />
        <DefaultSelectField
          label="User Role"
          value={config.userRole || 'patient'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            updateField('userRole', e.target.value)
          }
          inputElements={roleOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
          style={{ flex: 1 }}
        />
      </div>
      <div className="field-label">Title</div>
      <ParameterEditor
        value={config.title || ''}
        onChange={(value) => updateField('title', value)}
        placeholder="Notification title"
        parameters={eventSchema?.parameters || []}
        showPreview={true}
        style={{ marginBottom: '12px' }}
      />
      <div className="field-label">Message</div>
      <ParameterEditor
        value={config.message || ''}
        onChange={(value) => updateField('message', value)}
        placeholder="Push notification message"
        parameters={eventSchema?.parameters || []}
        showPreview={true}
      />
    </div>
  );
};

const TeamsActionForm: React.FC<
  ActionFormProps & { onChange: (config: TeamsActionConfig) => void }
> = ({ action, eventSchema, onChange }) => {
  const config: TeamsActionConfig = {
    message: '',
    channel: '',
    ...action.config,
  };

  const updateField = (field: keyof TeamsActionConfig, value: string) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="teams-action-form">
      <DefaultTextField
        label="Channel (Optional)"
        value={config.channel || ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          updateField('channel', e.target.value)
        }
        placeholder="general"
        style={{ marginBottom: '12px' }}
      />
      <div className="field-label">Message</div>
      <ParameterEditor
        value={config.message || ''}
        onChange={(value) => updateField('message', value)}
        placeholder="Teams message content"
        parameters={eventSchema?.parameters || []}
        showPreview={true}
      />
    </div>
  );
};

export default ConditionsAndActionsBuilder;
